import type { ToolUseBlock } from '../store/chatStore';

interface ToolUseMeta {
  humanReadableName?: string;
}

export const getToolUseMeta = (toolUse: ToolUseBlock): ToolUseMeta => {
  if (!toolUse.meta) {
    return {};
  }

  // check if meta is an object
  if (
    typeof toolUse.meta === 'object' &&
    toolUse.meta !== null &&
    'humanReadableName' in toolUse.meta
  ) {
    return {
      humanReadableName: toolUse.meta.humanReadableName as string,
    };
  }
  return {};
};

/**
 * Extracts the intention from a tool use block's input, falling back to the tool name if no intention is found
 * @param toolUse The tool use block to extract intention from
 * @returns The intention string or tool name as fallback
 */
export const getToolUseDisplay = (toolUse: ToolUseBlock): string => {
  try {
    if (typeof toolUse.input === 'object') {
      // If intention exists in the parsed data, return it
      if (
        toolUse.input?.intention &&
        typeof toolUse.input.intention === 'string'
      ) {
        return toolUse.input.intention;
      }
    }

    const toolUseMeta = getToolUseMeta(toolUse);
    if (toolUseMeta.humanReadableName) {
      return toolUseMeta.humanReadableName;
    }

    // Friendly fallbacks for activity-pill tools without an intention
    if (toolUse.name === 'loadSkill') {
      const names =
        (toolUse.input?.names as string[] | undefined) ??
        (toolUse.input?.name ? [toolUse.input.name as string] : []);
      if (names.length === 1) return `Applied ${names[0]}`;
      if (names.length > 1) return `Applied ${names.length} skills`;
      return 'Skill applied';
    }
    if (toolUse.name === 'getSkillResource') {
      const filePaths = (toolUse.input?.filePaths as string[] | undefined) ?? [];
      if (filePaths.length > 1) return `Loaded ${filePaths.length} files`;
      return `Loaded ${filePaths[0] ?? 'resource'}`;
    }
    if (toolUse.name === 'loadTools') {
      return 'Tools loaded';
    }

    return toolUse.name;
  } catch (error) {
    // If JSON parsing fails, silently fall back to name
  }

  // Fallback to tool name if no intention found or parsing failed
  return toolUse.name;
};
